import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgramWithStudentStatus, CourseWithGrades } from '../../../../types';

interface CardCoursesViewProps {
  program: ProgramWithStudentStatus;
  isProgramExpanded: (programId: number) => boolean;
  openGradeDialog: (course: CourseWithGrades) => void;
}

export const CardCoursesView: React.FC<CardCoursesViewProps> = ({
  program,
  isProgramExpanded,
  openGradeDialog,
}) => {
  const [expandedCourses, setExpandedCourses] = useState<
    Record<string, boolean>
  >({});

  const toggleCourseExpand = (programId: number, courseId: number) => {
    const key = `${programId}-${courseId}`;
    setExpandedCourses((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isCourseExpanded = (programId: number, courseId: number) => {
    const key = `${programId}-${courseId}`;
    return !!expandedCourses[key];
  };

  const statusBodyTemplate = (status: string) => {
    const getStatusSeverity = (status: string) => {
      switch (status) {
        case 'לומד':
          return 'success';
        case 'נרשם':
          return 'info';
        case 'ביטל':
          return 'danger';
        case 'עזב':
          return 'warning';
        case 'סיים':
          return 'success';
        case 'דופלם':
          return 'info';
        default:
          return 'warning';
      }
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'לומד':
          return 'pi pi-user';
        case 'נרשם':
          return 'pi pi-check-circle';
        case 'ביטל':
          return 'pi pi-times-circle';
        case 'עזב':
          return 'pi pi-sign-out';
        case 'סיים':
          return 'pi pi-check-square';
        case 'דופלם':
          return 'pi pi-star';
        default:
          return 'pi pi-question';
      }
    };

    return (
      <Tag
        value={status}
        severity={getStatusSeverity(status)}
        icon={getStatusIcon(status)}
        className="program-status-tag"
      />
    );
  };

  if (!isProgramExpanded(program.id)) {
    return null;
  }

  return (
    <div className="courses-cards">
      {program.courses.map((course) => (
        <Card key={course.id} className="course-card">
          <div
            className="card-summary"
            onClick={() => toggleCourseExpand(program.id, course.id)}
          >
            <div className="card-header">
              <h3>
                {course.courseName} - {course.additionalCourseName}
              </h3>
              <div className="card-header-right">
                {statusBodyTemplate(course.status)}
                <Button
                  icon={
                    isCourseExpanded(program.id, course.id)
                      ? 'pi pi-chevron-up'
                      : 'pi pi-chevron-down'
                  }
                  className="p-button-text p-button-rounded expand-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCourseExpand(program.id, course.id);
                  }}
                  aria-label={
                    isCourseExpanded(program.id, course.id)
                      ? 'Collapse'
                      : 'Expand'
                  }
                />
              </div>
            </div>
            <div className="card-score">
              <span className="detail-label">ציון סופי:</span>
              <span className="detail-value grade-final">
                {course.finalScore}
              </span>
            </div>
          </div>

          <div
            className={`card-details ${isCourseExpanded(program.id, course.id) ? 'expanded' : 'collapsed'}`}
          >
            <Divider />
            <div className="detail-item">
              <span className="detail-label">כמות שעות:</span>
              <span className="detail-value">{course.courseHours}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">נוכחות:</span>
              <span className="detail-value">
                {course.attendanceCount} שיעורים
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">אחוז נוכחות:</span>
              <span className="detail-value">
                {course.attendancePercentage}%
              </span>
            </div>

            <div className="card-actions">
              <Button
                label="פירוט ציונים"
                icon="pi pi-chart-pie"
                className="p-button-text view-grades-button"
                onClick={(e) => {
                  e.stopPropagation();
                  openGradeDialog(course);
                }}
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
